import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import LandApplicationContent from './LandApplicationContent';
import LandApplicationModal from '../modals/LandApplicationModal';
import ViewLogsModal from '../modals/ViewLogsModal';
import ViewSOAModal from '../modals/ViewSOAModal';

const form = {
  keyword: '',
  status: '',
};
const statusOptions = [
  { label: 'ALL STATUS', value: '' },
  { label: 'PENDING', value: 'PENDING' },
  { label: 'POSTED', value: 'POSTED' },
  {
    label: 'FOR APPRAISAL AND ASSESSMENT',
    value: 'FOR APPRAISAL AND ASSESSMENT',
  },
  { label: 'TRANSFERRED', value: 'TRANSFERRED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

function LandApplicationContainer({
  match,
  history,
  getLandApplicationsList,
  getSingleLandApplication,
  applicationList,
  cancelPost,
  postApplication,
  revertApplication,
  isLoading,
  applicationListPager,
  ordinanceRevisionId,
}) {
  const [formData, setFormData] = useState(form);
  const [filter, setFilter] = useState(form);
  const [toggleSearch, setToggleSearch] = useState(false);
  const setModal = useContext(ModalContext);

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <LandApplicationModal type="NEW" />,
      title: 'Add Land Application',
      modalSize: 'modal-xl',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <LandApplicationModal
          updateList={data.attributes}
          id={data.id}
          applicationInfo={data}
          applicationType={_.get(data, 'included.transactionCode.attributes')}
          type="EDIT"
        />
      ),
      title: 'Update Land Application',
      modalSize: 'modal-xl',
    });
  };

  const handleCancel = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to unpost this application?`,
      (remarks) => {
        cancelPost(_.get(data, 'id'), { remarks }, () => {
          getLandApplicationsList();
          history.push(`/rpt/land/applications/${_.get(data, 'id')}`);
          getSingleLandApplication(_.get(data, 'id'));
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      true
    );
  };

  const handlePost = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to post this application?`,
      () => {
        postApplication(_.get(data, 'id'), () => {
          getLandApplicationsList();
          history.push(`/rpt/land/applications/${_.get(data, 'id')}`);
          getSingleLandApplication(_.get(data, 'id'));
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      false
    );
  };

  const handleRevert = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to revert this application?`,
      () => {
        revertApplication(_.get(data, 'id'), () => {
          getLandApplicationsList();
          history.push(`/rpt/land/applications/${_.get(data, 'id')}`);
          getSingleLandApplication(_.get(data, 'id'));
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      false
    );
  };

  useEffect(() => {
    getLandApplicationsList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const param = {
    [toggleSearch ? 'mother_tdn' : 'q']: formData.keyword,
    status: filter.status,
    sort_by: 'asc',
  };

  const onSetStatusFilter = (key) => (e) => {
    setFilter({
      ...filter,
      [key]: e.value,
    });
    getLandApplicationsList({
      ...param,
      [key]: e.value, // status
    });
  };

  const debouncedSearchTerm = useDebounce(formData, 500);

  useEffect(() => {
    if (debouncedSearchTerm) {
      setFilter({
        ...filter,
        [toggleSearch ? 'mother_tdn' : 'q']: formData.keyword,
      });
      getLandApplicationsList(param);
    } else {
      setFormData([]);
      getLandApplicationsList({
        sort_by: 'asc',
        status: filter.status,
      });
    }
  }, [debouncedSearchTerm, toggleSearch]);

  const handleLogsModal = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <ViewLogsModal data={data} />,
      title: 'Land Application Logs',
      modalSize: 'modal-lg',
    });
  };

  const handleViewSOA = (data, id) => (e) => {
    e.preventDefault();

    setModal({
      isOpen: true,
      content: <ViewSOAModal data={data} applicationId={id} />,
      title: `View e-SOA`,
      modalSize: 'modal-xl',
    });
  };

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:applicationId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Land Applications"
            getData={getLandApplicationsList}
            searchToggle={{
              label: 'Mother TDN',
              value: toggleSearch,
              toggle: setToggleSearch,
            }}
            setParams={param}
            setKeyword={handleOnChange}
            setStatusFilter={onSetStatusFilter('status')}
            status={_.get(filter, 'status') || ''}
            statusOptions={statusOptions}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:applicationId?`}
            listName="Land Application"
            label="Land Applications"
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.tdn')}</div>
            )}
            isLoading={isLoading}
            statusFilter
            selected={params.applicationId}
            data={applicationList}
            dataPager={applicationListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:applicationId?`}
                    render={() => (
                      <LandApplicationContent
                        list={applicationList}
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleCancel={handleCancel}
                        handlePost={handlePost}
                        handleRevert={handleRevert}
                        // handleMemoranda={handleMemoranda}
                        handleLogsModal={handleLogsModal}
                        handleViewSOA={handleViewSOA}
                        ordinanceRevisionId={ordinanceRevisionId}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:applicationId?`}
                  render={() =>
                    !params.applicationId && applicationList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(applicationList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

LandApplicationContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  cancelPost: PropTypes.instanceOf(Function).isRequired,
  postApplication: PropTypes.instanceOf(Function).isRequired,
  revertApplication: PropTypes.instanceOf(Function).isRequired,
  getLandApplicationsList: PropTypes.instanceOf(Function).isRequired,
  getSingleLandApplication: PropTypes.instanceOf(Function).isRequired,
  applicationList: PropTypes.instanceOf(Array).isRequired,
  applicationListPager: PropTypes.instanceOf(Object).isRequired,
  ordinanceRevisionId: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ api }) => ({
  applicationList: _.get(api, `${c.GET_LAND_APPLICATIONS}.list`) || [],
  applicationListPager: _.get(api, `${c.GET_LAND_APPLICATIONS}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_LAND_APPLICATIONS) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(LandApplicationContainer);
